import type { AnyRecord } from './api'

export interface PcVipPlanPresentation {
  available: boolean
  planId: number | null
  period: 'year' | 'week' | 'month' | ''
  title: string
  primary: string
  secondary: string
  renewal: string
  cta: string
}

const PERIOD_BY_DURATION_TYPE: Record<number, string> = { 1: 'month', 3: 'year', 4: 'week' }
const PLAN_PERIODS = ['year', 'week', 'month'] as const

function enabled(value: unknown): boolean {
  return value === true || value === 1 || value === '1'
}

function presentPlan(plan: AnyRecord): PcVipPlanPresentation | null {
  const id = Number(plan?.id)
  const rawPrice = String(plan?.price ?? '').trim()
  const amount = Number(rawPrice)
  const currency = String(plan?.currency || 'USD').trim().toUpperCase()
  const period = String(plan?.subscription_period_text || PERIOD_BY_DURATION_TYPE[Number(plan?.duration_type)] || '')
  // This endpoint contains USD Stripe packages. Never manufacture an intro price
  // or silently correct an amount that differs from the checkout configuration.
  if (!Number.isSafeInteger(id) || id <= 0
    || !/^price_\w+$/.test(String(plan?.stripe_price_id || ''))
    || !enabled(plan?.is_subscription)
    || enabled(plan?.has_first_month_discount)
    || String(plan?.stripe_coupon_id || '').trim()
    || !['USD', '$', 'US$'].includes(currency)
    || !/^\d+(?:\.\d{1,2})?$/.test(rawPrice) || !Number.isFinite(amount) || amount <= 0
    || !PLAN_PERIODS.includes(period as typeof PLAN_PERIODS[number])) return null

  const unit = period as 'year' | 'week' | 'month'
  const price = `$${amount.toFixed(2)}`
  const recurring = `${price}/${unit}`
  return {
    available: true,
    planId: id,
    period: unit,
    title: { year: 'Yearly', week: 'Weekly', month: 'Monthly' }[unit],
    primary: recurring,
    secondary: `Billed ${price} every ${unit}`,
    renewal: `Pay ${price} today. Renews at ${recurring} until canceled.`,
    cta: `Continue · ${recurring}`,
  }
}

export function buildPcVipPlans(payload: AnyRecord | null | undefined): PcVipPlanPresentation[] {
  const rawPlans = Array.isArray(payload?.vip_packages) ? payload.vip_packages : []
  const plans = rawPlans.map(presentPlan).filter((plan: PcVipPlanPresentation | null): plan is PcVipPlanPresentation => Boolean(plan))
  return PLAN_PERIODS.flatMap(period => {
    const plan = plans.find((item: PcVipPlanPresentation) => item.period === period)
    return plan ? [plan] : []
  })
}

export function buildPcVipPlanPresentation(payload: AnyRecord | null | undefined, selectedPlanId: number | null = null): PcVipPlanPresentation {
  const plans = buildPcVipPlans(payload)
  // An explicit choice that becomes unavailable must not change to another bill.
  const plan = selectedPlanId === null ? plans[0] : plans.find(item => item.planId === selectedPlanId)
  return plan || {
    available: false,
    planId: null,
    period: '',
    title: '',
    primary: 'Plans temporarily unavailable',
    secondary: 'Please refresh to see current plans.',
    renewal: 'Choose an available plan before continuing to checkout.',
    cta: 'Choose an available plan',
  }
}

export function buildPcVipCheckoutUrl(planId: number | null, context: Record<string, string> = {}): string | null {
  if (!Number.isSafeInteger(planId) || Number(planId) <= 0) return null
  const url = new URL('https://reelora.vip/pagesUser/subscribe/subscribe')
  url.searchParams.set('plan_id', String(planId))
  for (const key of ['drama_id', 'episode']) {
    const value = Number(context[key])
    if (Number.isSafeInteger(value) && value > 0) url.searchParams.set(key, String(value))
  }
  url.searchParams.set('source', String(context.source || 'pc_membership').slice(0, 80))
  return url.toString()
}

export function hasActivePcMembership(profile: AnyRecord | null | undefined, nowMs = Date.now()): boolean {
  const user = profile?.userInfo || profile || {}
  const rawExpiry = user.vip_expire_time
  if (rawExpiry) {
    const numeric = Number(rawExpiry)
    const expires = Number.isFinite(numeric) ? numeric * (numeric < 1e12 ? 1000 : 1) : Date.parse(String(rawExpiry))
    if (Number.isFinite(expires)) return expires > nowMs
  }
  return enabled(user.vip) || enabled(user.is_vip)
}
